/**
 * Brand logos for the Supported Technologies row.
 *
 * Strategy:
 *   - For widely-recognised tech brands we use the simpleicons.org CDN
 *     (MIT-licensed canonical SVGs).
 *   - For brands that simpleicons doesn\u2019t serve (Tally Solutions, GST,
 *     Microsoft Excel, Salesforce, Oracle, SAP) we draw a clean inline SVG
 *     wordmark in the brand's primary colour. That way the page is fully
 *     self-contained and never shows a broken image.
 *
 * All logos default to grayscale + ~70% opacity on the page and animate
 * to full color on hover \u2014 the standard pattern for an integrations row.
 */

const CDN = (slug) => `https://cdn.simpleicons.org/${slug}`;

function TallyMark() {
    return (
        <svg viewBox="0 0 120 48" width="120" height="48" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Tally">
            <rect x="2" y="6" width="36" height="36" rx="8" fill="#C8202F" />
            <text x="20" y="32" textAnchor="middle" fontFamily="Inter, Arial, sans-serif" fontSize="20" fontWeight="800" fill="#ffffff">T</text>
            <text x="46" y="32" fontFamily="Inter, Arial, sans-serif" fontSize="20" fontWeight="700" fill="#1E3A5F" letterSpacing="-0.5">Tally</text>
        </svg>
    );
}

function GstMark() {
    return (
        <svg viewBox="0 0 120 48" width="120" height="48" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="GST">
            <g>
                <rect x="2" y="10" width="11" height="28" fill="#F97316" />
                <rect x="13" y="10" width="11" height="28" fill="#ffffff" stroke="#CBD5E1" strokeWidth="0.6" />
                <rect x="24" y="10" width="11" height="28" fill="#059669" />
                <circle cx="18.5" cy="24" r="3.6" fill="none" stroke="#1E3A5F" strokeWidth="0.9" />
                <g stroke="#1E3A5F" strokeWidth="0.7">
                    <line x1="18.5" y1="20.4" x2="18.5" y2="27.6" />
                    <line x1="14.9" y1="24" x2="22.1" y2="24" />
                </g>
            </g>
            <text x="46" y="32" fontFamily="Inter, Arial, sans-serif" fontSize="22" fontWeight="800" fill="#1E3A5F" letterSpacing="-0.5">GST</text>
        </svg>
    );
}

function ExcelMark() {
    return (
        <svg viewBox="0 0 130 48" width="130" height="48" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Microsoft Excel">
            <rect x="2" y="6" width="36" height="36" rx="6" fill="#107C41" />
            <text x="20" y="32" textAnchor="middle" fontFamily="Inter, Arial, sans-serif" fontSize="22" fontWeight="800" fill="#ffffff">X</text>
            <text x="46" y="32" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700" fill="#1E3A5F" letterSpacing="-0.4">Excel</text>
        </svg>
    );
}

function SalesforceMark() {
    return (
        <svg viewBox="0 0 150 48" width="150" height="48" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Salesforce">
            {/* stylised cloud */}
            <g transform="translate(2,12) scale(1)">
                <path
                    d="M10 18 C 6 18 3 15 3 12 C 3 9 6 6 9.5 7 C 10.5 4 13.5 2 17 2 C 21 2 24 5 24.5 8 C 28 7.5 31 10 31 13.5 C 31 16.5 28.5 19 25 19 L 12 19 C 11.2 19 10.6 18.5 10 18 Z"
                    fill="#00A1E0"
                />
            </g>
            <text x="40" y="32" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700" fill="#1E3A5F" letterSpacing="-0.4">Salesforce</text>
        </svg>
    );
}

function OracleMark() {
    return (
        <svg viewBox="0 0 130 48" width="130" height="48" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Oracle">
            <ellipse cx="65" cy="24" rx="46" ry="13" fill="none" stroke="#C74634" strokeWidth="5" />
            <text x="65" y="30" textAnchor="middle" fontFamily="Inter, Arial, sans-serif" fontSize="16" fontWeight="800" fill="#C74634" letterSpacing="2">ORACLE</text>
        </svg>
    );
}

function SapMark() {
    // SAP wordmark in their signature blue gradient style.
    return (
        <svg viewBox="0 0 130 48" width="130" height="48" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="SAP">
            <defs>
                <linearGradient id="sapg" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#003F7D" />
                    <stop offset="100%" stopColor="#008FD3" />
                </linearGradient>
            </defs>
            <rect x="6" y="10" width="118" height="28" rx="3" fill="url(#sapg)" />
            <text x="65" y="32" textAnchor="middle" fontFamily="Inter, Arial, sans-serif" fontSize="22" fontWeight="800" fill="#ffffff" letterSpacing="2">SAP</text>
        </svg>
    );
}

function RazorpayMark() {
    // Razorpay \u2014 stylised "R" in their signature dark navy blue.
    return (
        <svg viewBox="0 0 150 48" width="150" height="48" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Razorpay">
            <g transform="translate(4,8)">
                <polygon points="0,32 16,0 26,0 18,16 22,16 12,32" fill="#3395FF" />
                <polygon points="14,16 30,16 22,32 14,32" fill="#072654" />
            </g>
            <text x="40" y="32" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700" fill="#072654" letterSpacing="-0.4">Razorpay</text>
        </svg>
    );
}

function DynamicsMark() {
    // Microsoft Dynamics \u2014 Microsoft 4-quadrant tile + Dynamics wordmark.
    return (
        <svg viewBox="0 0 170 48" width="170" height="48" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Microsoft Dynamics">
            <g transform="translate(6,8)">
                <rect x="0"  y="0"  width="14" height="14" fill="#F25022" />
                <rect x="16" y="0"  width="14" height="14" fill="#7FBA00" />
                <rect x="0"  y="16" width="14" height="14" fill="#00A4EF" />
                <rect x="16" y="16" width="14" height="14" fill="#FFB900" />
            </g>
            <text x="44" y="22" fontFamily="Inter, Arial, sans-serif" fontSize="11" fontWeight="500" fill="#64748B" letterSpacing="0.04em">Microsoft</text>
            <text x="44" y="36" fontFamily="Inter, Arial, sans-serif" fontSize="16" fontWeight="700" fill="#1E3A5F" letterSpacing="-0.3">Dynamics</text>
        </svg>
    );
}

function NetSuiteMark() {
    // Oracle NetSuite \u2014 simple N-mark + wordmark in their signature deep red.
    return (
        <svg viewBox="0 0 160 48" width="160" height="48" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="NetSuite">
            <g transform="translate(4,8)">
                <rect x="0" y="0" width="32" height="32" rx="6" fill="#125CA1" />
                <path d="M 8 24 L 8 8 L 12 8 L 22 18 L 22 8 L 26 8 L 26 24 L 22 24 L 12 14 L 12 24 Z" fill="#ffffff" />
            </g>
            <text x="40" y="32" fontFamily="Inter, Arial, sans-serif" fontSize="17" fontWeight="700" fill="#1E3A5F" letterSpacing="-0.4">NetSuite</text>
        </svg>
    );
}

function QuickBooksMark() {
    // Intuit QuickBooks \u2014 circular QB + wordmark in their signature green.
    return (
        <svg viewBox="0 0 170 48" width="170" height="48" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="QuickBooks">
            <g transform="translate(4,4)">
                <circle cx="20" cy="20" r="19" fill="#2CA01C" />
                <text x="20" y="27" textAnchor="middle" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="800" fill="#ffffff" letterSpacing="-1">qb</text>
            </g>
            <text x="48" y="22" fontFamily="Inter, Arial, sans-serif" fontSize="11" fontWeight="500" fill="#64748B" letterSpacing="0.04em">Intuit</text>
            <text x="48" y="36" fontFamily="Inter, Arial, sans-serif" fontSize="15" fontWeight="700" fill="#2CA01C" letterSpacing="-0.3">QuickBooks</text>
        </svg>
    );
}

export const LOGOS = [
    { name: "Tally", node: <TallyMark /> },
    {
        name: "Zoho",
        node: <img src={CDN("zoho")} alt="Zoho" />,
    },
    {
        name: "Odoo",
        node: <img src={CDN("odoo")} alt="Odoo" />,
    },
    { name: "Microsoft Excel", node: <ExcelMark /> },
    { name: "QuickBooks", node: <QuickBooksMark /> },
    { name: "GST", node: <GstMark /> },
    {
        name: "Google",
        node: <img src={CDN("google")} alt="Google" />,
    },
    { name: "Razorpay", node: <RazorpayMark /> },
    { name: "Salesforce", node: <SalesforceMark /> },
    { name: "MS Dynamics", node: <DynamicsMark /> },
    { name: "NetSuite", node: <NetSuiteMark /> },
    { name: "Oracle", node: <OracleMark /> },
    { name: "SAP", node: <SapMark /> },
];
